import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { homeFor } from "../lib/roles";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    if (user) return <Navigate to={homeFor(user.role)} replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const u = await login(email, password);
            navigate(homeFor(u?.role));
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || "Sign in failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-ivory overflow-x-hidden">
            <div className="hidden lg:block relative">
                <img
                    src="https://customer-assets-wrfwihn1.emergentagent.net/job_jewelry-hub-198/artifacts/70d9bf943be97528_0BD31CF3-1F49-4490-994E-FDABD2D4E26D.png"
                    alt=""
                    draggable={false}
                    className="absolute inset-0 w-full h-full object-cover select-none"
                />
            </div>
            <div className="flex items-center justify-center p-6 md:p-10 lg:p-16">
                <form onSubmit={onSubmit} className="w-full max-w-sm space-y-7" data-testid="login-form">
                    <div className="flex flex-col items-start gap-5">
                        <div className="leading-tight" data-testid="login-branding">
                            <div className="font-serif text-2xl tracking-tight text-ink">H&amp;T GROUP</div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">TEAM PORTAL</div>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div>
                            <label className="eyebrow">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="w-full border-b border-mist focus:border-sage-dark outline-none py-2 text-base bg-transparent transition-colors"
                            />
                        </div>
                        <div>
                            <label className="eyebrow">Password</label>
                            <div className="relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-password-input"
                                    className="w-full border-b border-mist focus:border-sage-dark outline-none py-2 pr-9 text-base bg-transparent transition-colors"
                                />
                                <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-ink" data-testid="login-password-toggle">
                                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="text-sm text-red-600" data-testid="login-error">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="login-submit-button"
                        className="w-full bg-sage-dark text-white py-3 rounded-sm uppercase text-xs tracking-widest hover:bg-sage-deep transition-colors disabled:opacity-60"
                    >
                        {busy ? "Loading…" : "Sign in"}
                    </button>

                    <div className="text-[11px] text-neutral-400 border-t border-mist pt-4 leading-relaxed">
                        Browsing instead?{" "}
                        <a href="/" className="underline hover:text-sage-dark">Choose a catalog</a>
                    </div>
                </form>
            </div>
        </div>
    );
}
